import { ScrollTextIcon } from '@/components/atoms/icons';
import { Accordion, DocumentItem, OkDialog } from '@/components/molecules';
import { useTranslation, useUIColors } from '@/core/hooks';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import { shareFile } from '@/core/wrappers/sharing';
import { downloadAndPersistBrochureFiles } from '@/core/utils/brochureDownload';
import { DOCUMENT_TYPES, PropertyDocument } from '@/modules/profile/constants/documentTypes';
import { downloadAndPersistDocument } from '@/modules/property-details/utils/documentDownload';
import { getPropertyDocuments } from '@/modules/property-details/utils/propertyDocuments';
import { router } from 'expo-router';
import React, { useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';

export interface PropertyDocumentsAccordionProps {
    salesOrderId: string; // salesorder_id from property (required for property documents)
    unitId?: string;
    unitLabel?: string;
    documents :PropertyDocument
    brochureUrls?: ReadonlyArray<string>;
    projectName?: string;
    /** Fired when a document row is tapped, before the download starts (e.g. analytics). */
    onDocumentPress?: (docType: string) => void;
}

type DownloadResultLike = {
    success: boolean;
    fileUri?: string;
    fileName?: string;
    message?: string;
    error?: string;
    requiresShareToSave?: boolean;
};

function resolveDocumentParentId(
    docType: string,
    unitId: string | undefined,
    salesOrderId: string,
): string {
    if (docType === DOCUMENT_TYPES.FLOOR_PLAN && unitId) {
        return unitId;
    }
    return salesOrderId;
}

export function PropertyDocumentsAccordion({ salesOrderId, unitId, unitLabel, documents, brochureUrls, projectName, onDocumentPress }: Readonly<PropertyDocumentsAccordionProps>) {
    const { t } = useTranslation();
    const colors = useUIColors();
    const user = useAppStore((state) => state.auth.user);
    const customerId = user?.customer_id;

    const [downloadingDocType, setDownloadingDocType] = useState<string | null>(null);
    const [errorDialogVisible, setErrorDialogVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successDialogVisible, setSuccessDialogVisible] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [savedFileUri, setSavedFileUri] = useState<string | null>(null);
    const [savedFileName, setSavedFileName] = useState<string>('');
    const [savedRequiresShare, setSavedRequiresShare] = useState(false);

    const showError = (message: string) => {
        setErrorMessage(message);
        setErrorDialogVisible(true);
    };

    const applyDownloadResult = (downloadResult: DownloadResultLike, fallbackError: string) => {
        if (downloadResult.success && downloadResult.fileUri && downloadResult.message) {
            setSuccessMessage(downloadResult.message);
            setSavedFileUri(downloadResult.fileUri);
            setSavedFileName(downloadResult.fileName || '');
            setSavedRequiresShare(Boolean(downloadResult.requiresShareToSave));
            setSuccessDialogVisible(true);
            return;
        }
        showError(downloadResult.error || fallbackError);
    };

    // Get all property documents from utility
    const propertyDocuments = getPropertyDocuments(documents);

    // AC-07/AC-13: only list Brochure when at least one URL is actually
    // available — no entry that just errors out on tap.
    const displayedDocuments = [
        ...(brochureUrls && brochureUrls.length > 0
            ? [
                {
                    docType: 'BROCHURE' as any,
                    translationKey: 'propertyDetails.brochure',
                    name: 'Brochure' as any,
                    order: 0,
                },
            ]
            : []),
        ...propertyDocuments,
    ];

    const downloadBrochure = async () => {
        if (!brochureUrls || brochureUrls.length === 0) {
            showError(t('propertyDetails.brochureNotAvailable'));
            return;
        }

        setDownloadingDocType('BROCHURE');
        try {
            const downloadResult = await downloadAndPersistBrochureFiles(brochureUrls, projectName || unitLabel || 'Property', t as any);
            applyDownloadResult(downloadResult, t('propertyDetails.documentSaveFailed'));
        } catch (err: any) {
            logger.error('PropertyDocumentsAccordion', 'Failed to download brochure', {
                error: err?.message || err,
            });
            showError(err?.message || t('propertyDetails.brochureDownloadFailed'));
        } finally {
            setDownloadingDocType(null);
        }
    };

    const openAccountStatementRequest = () => {
        router.push({
            pathname: '/service-request-create',
            params: {
                categoryName: 'Download My Statement of Account',
                description: 'I want to download my account statement',
                unitId: unitId || '',
                unitLabel: unitLabel || '',
            },
        } as any);
    };

    const downloadTypedDocument = async (docType: string, translationKey: string) => {
        if (!customerId) {
            showError(t('propertyDetails.customerIdNotAvailable'));
            return;
        }

        setDownloadingDocType(docType);

        try {
            const parentId = resolveDocumentParentId(docType, unitId, salesOrderId);

            logger.info('PropertyDocumentsAccordion', 'Downloading document', {
                docType,
                salesOrderId,
                customerId,
            });

            const downloadResult = await downloadAndPersistDocument({
                docType: docType as any,
                parentId: parentId,
                customerId,
                translationKey: translationKey as string,
                t: t as any,
            });

            applyDownloadResult(downloadResult, t('propertyDetails.documentSaveFailed'));
        } catch (error: any) {
            logger.error('PropertyDocumentsAccordion', 'Failed to download document', {
                docType,
                error: error?.message || error,
            });
            showError(error?.message || t('propertyDetails.documentDownloadFailed'));
        } finally {
            setDownloadingDocType(null);
        }
    };

    const handleDocumentPress = async (docType: string, translationKey: string) => {
        onDocumentPress?.(docType);
        if (docType === 'BROCHURE') {
            await downloadBrochure();
            return;
        }

        if (docType === 'account-statement-request') {
            openAccountStatementRequest();
            return;
        }

        await downloadTypedDocument(docType, translationKey);
    };

    if (displayedDocuments.length === 0) {
        return null;
    }

    return (
        <>
            <Accordion
                icon={ScrollTextIcon}
                title={t('propertyDetails.documents')}
                contentPadding={16}
                defaultExpanded={true}
            >
                <View style={styles.documentsList}>
                    {displayedDocuments.map((documentDetails) => {
                        const isDownloading = downloadingDocType === documentDetails.docType;
                        const docTitle = t(documentDetails.translationKey as any) || documentDetails.docType;

                        return (
                            <View key={documentDetails.docType} style={styles.documentItemWrapper}>
                                <DocumentItem
                                    id={documentDetails.name}
                                    title={docTitle}
                                    type="PDF"
                                    downloadUrl=""
                                    onPress={() => !isDownloading && handleDocumentPress(documentDetails.docType, documentDetails.translationKey)}
                                />
                                {isDownloading && (
                                    <View style={[styles.loadingOverlay, { backgroundColor: colors.backgroundLevel1 }]}>
                                        <ActivityIndicator size="small" color={colors.fontPrimary} />
                                    </View>
                                )}
                            </View>
                        );
                    })}
                </View>
            </Accordion>

            <OkDialog
                visible={errorDialogVisible}
                message={errorMessage}
                onOk={() => setErrorDialogVisible(false)}
            />

            <OkDialog
                visible={successDialogVisible}
                title={t('common.downloadSuccessTitle')}
                message={successMessage}
                okText={Platform.OS === 'ios' || savedRequiresShare ? t('common.saveShare') : t('common.ok')}
                onOk={async () => {
                    if ((Platform.OS === 'ios' || savedRequiresShare) && savedFileUri) {
                        const shareResult = await shareFile({ uri: savedFileUri });
                        if (!shareResult.success && shareResult.error) {
                            logger.warn('PropertyDocumentsAccordion', 'Sharing failed', {
                                error: shareResult.error,
                                fileUri: savedFileUri,
                            });
                        }
                    }
                    setSuccessDialogVisible(false);
                    setSavedFileUri(null);
                    setSavedFileName('');
                    setSavedRequiresShare(false);
                }}
            />
        </>
    );
}

const styles = StyleSheet.create({
    documentsList: {
        gap: 8,
    },
    documentItemWrapper: {
        position: 'relative',
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 8,
        opacity: 0.8,
    },
});

export default PropertyDocumentsAccordion;
